/**
 * Mini visualizations for the welcome/orientation page.
 * Each shows an animated preview of a PaperChase stage.
 */

// Catppuccin Macchiato palette subset (default). The pre-auth landing passes a
// brand palette with the same keys via the `palette` prop to recolor these minis.
const BASE = {
  surface0: "#363a4f",
  overlay0: "#6e738d",
  text: "#cad3f5",
  subtext: "#a5adcb",
  mauve: "#c6a0f6",
  teal: "#8bd5ca",
  peach: "#f5a97f",
  yellow: "#eed49f",
  green: "#a6da95",
  red: "#ed8796",
  blue: "#8aadf4",
  sapphire: "#7dc4e4",
};

export type MiniPalette = typeof BASE;

export function ConceptMini({ visible, palette = BASE }: { visible: boolean; palette?: MiniPalette }) {
  const C = palette;
  return (
    <div
      style={{
        width: "100%",
        maxWidth: 240,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.6s ease",
      }}
    >
      <div
        style={{
          padding: "12px 14px",
          borderRadius: 8,
          backgroundColor: `${C.surface0}60`,
          border: `1px solid ${C.mauve}25`,
        }}
      >
        <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", color: C.mauve, marginBottom: 6, fontWeight: 600 }}>
          Concept
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
          Transformer Attention Mechanisms in Low-Resource NLP
        </div>
        <div style={{ fontSize: 11, lineHeight: 1.5, color: C.subtext }}>
          Investigating how attention-based architectures can be adapted for languages with limited training data...
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              backgroundColor: C.mauve,
              opacity: visible ? 0.4 : 0,
              transition: `opacity 0.3s ease ${i * 100 + 400}ms`,
            }}
          />
        ))}
        <div style={{ fontSize: 9, color: C.overlay0, marginLeft: 4 }}>3 seeds</div>
      </div>
    </div>
  );
}

export function DiscoveryMini({ visible, palette = BASE }: { visible: boolean; palette?: MiniPalette }) {
  const C = palette;
  const rings = [3, 6, 10];
  // Discovered papers cluster by source — contiguous angular wedges read as
  // distinct result groups (citation network, Spot Trawl, library import).
  const clusters = [C.teal, C.blue, C.peach];
  const clusterColor = (angle: number) => {
    const a = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    return clusters[Math.floor(a / ((Math.PI * 2) / clusters.length)) % clusters.length];
  };
  return (
    <svg width="200" height="160" viewBox="0 0 200 160">
      {rings.map((count, ringIdx) => {
        const radius = 20 + ringIdx * 25;
        return (
          <g key={ringIdx}>
            <circle
              cx="100"
              cy="80"
              r={visible ? radius : 0}
              fill="none"
              stroke={C.overlay0}
              strokeWidth="0.5"
              strokeOpacity="0.25"
              strokeDasharray="3 3"
              style={{ transition: `r 0.6s ease ${ringIdx * 200}ms` }}
            />
            {Array.from({ length: count }).map((_, i) => {
              const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
              const x = 100 + Math.cos(angle) * radius;
              const y = 80 + Math.sin(angle) * radius;
              const isSeed = ringIdx === 0;
              const color = isSeed ? C.mauve : clusterColor(angle);
              return (
                <circle
                  key={i}
                  cx={x}
                  cy={y}
                  r={visible ? (isSeed ? 4 : 3) : 0}
                  fill={`${color}${isSeed ? "40" : "33"}`}
                  stroke={color}
                  strokeWidth="1"
                  style={{ transition: `r 0.3s ease ${ringIdx * 200 + i * 50}ms` }}
                />
              );
            })}
          </g>
        );
      })}
      <text x="100" y="75" textAnchor="middle" dominantBaseline="central" fill={C.mauve} fontSize="11" fontWeight="700" opacity={visible ? 1 : 0} style={{ transition: "opacity 0.3s ease 0.2s" }}>
        Seeds
      </text>
    </svg>
  );
}

export function ScoringMini({ visible, palette = BASE }: { visible: boolean; palette?: MiniPalette }) {
  const C = palette;
  const papers = [
    { title: "Attention Is All You Need", score: 94, role: "supports", color: C.teal },
    { title: "BERT: Pre-training of Deep...", score: 87, role: "extends", color: C.blue },
    { title: "A Survey of Transformer...", score: 76, role: "reviews", color: C.sapphire },
    { title: "On the Dangers of Stochastic...", score: 71, role: "challenges", color: C.red },
    { title: "Scaling Laws for Neural...", score: 63, role: "supports", color: C.teal },
  ];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.8s ease",
      }}
    >
      {papers.map((p, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "6px 10px",
            borderRadius: 6,
            backgroundColor: `${C.surface0}80`,
            opacity: visible ? 1 : 0,
            transform: visible ? "translateX(0)" : "translateX(-8px)",
            transition: `all 0.4s ease ${i * 100}ms`,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: `${C.green}15`,
              border: `1.5px solid ${C.green}40`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              fontWeight: 700,
              color: C.green,
              flexShrink: 0,
            }}
          >
            {p.score}
          </div>
          <div style={{ flex: 1, fontSize: 11, color: C.subtext, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {p.title}
          </div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 600,
              padding: "2px 6px",
              borderRadius: 3,
              backgroundColor: `${p.color}15`,
              color: p.color,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              flexShrink: 0,
            }}
          >
            {p.role}
          </div>
        </div>
      ))}
    </div>
  );
}

export function DebateMini({ visible, palette = BASE }: { visible: boolean; palette?: MiniPalette }) {
  const C = palette;
  const camps = [
    { label: "Scaling Optimists", papers: 12, x: 12, y: 22, w: 72, color: C.teal },
    { label: "Efficiency Focus", papers: 8, x: 156, y: 22, w: 72, color: C.blue },
    { label: "Safety Critics", papers: 6, x: 84, y: 112, w: 72, color: C.red },
  ];

  return (
    <svg width="240" height="160" viewBox="0 0 240 160" style={{ opacity: visible ? 1 : 0, transition: "opacity 0.8s ease" }}>
      {/* Triangle connectors between camp centers — routed around the central marker */}
      <line x1="48" y1="40" x2="192" y2="40" stroke={C.overlay0} strokeWidth="1" strokeDasharray="3 3" strokeOpacity="0.3" />
      <line x1="48" y1="40" x2="120" y2="130" stroke={C.overlay0} strokeWidth="1" strokeDasharray="3 3" strokeOpacity="0.3" />
      <line x1="192" y1="40" x2="120" y2="130" stroke={C.overlay0} strokeWidth="1" strokeDasharray="3 3" strokeOpacity="0.3" />
      {camps.map((camp, i) => (
        <g key={i}>
          <rect x={camp.x} y={camp.y} width={visible ? camp.w : 0} height={visible ? 35 : 0} rx="6" fill={`${camp.color}12`} stroke={camp.color} strokeWidth="1" strokeOpacity="0.4" style={{ transition: `all 0.5s ease ${i * 150}ms` }} />
          <text x={camp.x + camp.w / 2} y={camp.y + 15} textAnchor="middle" fill={camp.color} fontSize="8" fontWeight="600" fontFamily="Outfit, system-ui" opacity={visible ? 1 : 0} style={{ transition: `opacity 0.5s ease ${i * 150 + 200}ms` }}>{camp.label}</text>
          <text x={camp.x + camp.w / 2} y={camp.y + 27} textAnchor="middle" fill={C.overlay0} fontSize="7" fontFamily="Outfit, system-ui" opacity={visible ? 0.7 : 0} style={{ transition: `opacity 0.5s ease ${i * 150 + 300}ms` }}>{camp.papers} papers</text>
        </g>
      ))}
      <circle cx="120" cy="83" r={visible ? 5 : 0} fill={C.mauve} fillOpacity="0.5" stroke={C.mauve} strokeWidth="1.5" style={{ transition: "r 0.4s ease 0.6s" }} />
      <text x="120" y="73" textAnchor="middle" fill={C.mauve} fontSize="7" fontWeight="600" fontFamily="Outfit, system-ui" opacity={visible ? 0.8 : 0} style={{ transition: "opacity 0.5s ease 0.7s" }}>You are here</text>
    </svg>
  );
}

export function ArgumentMini({ visible, palette = BASE }: { visible: boolean; palette?: MiniPalette }) {
  const C = palette;
  const nodes = [
    { id: "t", x: 140, y: 20, r: 16, label: "Your Thesis", color: C.green, labelColor: C.green },
    { id: "s1", x: 60, y: 70, r: 11, label: "Reason", color: C.teal },
    { id: "s2", x: 140, y: 80, r: 11, label: "Reason", color: C.teal },
    { id: "c1", x: 220, y: 65, r: 11, label: "Objection", color: C.red },
    { id: "e1", x: 30, y: 120, r: 7, color: C.peach },
    { id: "e2", x: 80, y: 125, r: 7, color: C.peach },
    { id: "e3", x: 130, y: 130, r: 7, color: C.peach },
    { id: "e4", x: 180, y: 125, r: 7, color: C.peach },
    { id: "e5", x: 230, y: 115, r: 7, color: C.peach },
    { id: "gap", x: 60, y: 160, r: 10, label: "Open question", color: C.yellow, dashed: true },
  ];

  const edges: [string, string][] = [
    ["t", "s1"], ["t", "s2"], ["t", "c1"],
    ["s1", "e1"], ["s1", "e2"],
    ["s2", "e3"], ["s2", "e4"],
    ["c1", "e5"],
    ["s1", "gap"],
  ];

  return (
    <svg width="280" height="196" viewBox="0 0 280 196" style={{ opacity: visible ? 1 : 0, transition: "opacity 0.8s ease" }}>
      {edges.map(([fromId, toId], i) => {
        const from = nodes.find((n) => n.id === fromId)!;
        const to = nodes.find((n) => n.id === toId)!;
        const isGap = toId === "gap";
        return (
          <line key={i} x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={to.color} strokeWidth="1" strokeOpacity={visible ? 0.4 : 0} strokeDasharray={isGap ? "3 3" : "none"} style={{ transition: `stroke-opacity 0.5s ease ${(i + 8) * 80}ms` }} />
        );
      })}
      {nodes.map((n, i) => (
        <g key={n.id}>
          <circle cx={n.x} cy={n.y} r={visible ? n.r : 0} fill={`${n.color}20`} stroke={n.color} strokeWidth="1.5" strokeDasharray={n.dashed ? "3 2" : "none"} style={{ transition: `r 0.4s ease ${i * 80}ms` }} />
          {n.label && (
            <text x={n.x} y={n.y + n.r + 12} textAnchor="middle" fill={n.labelColor || n.color} fontSize="9" fontWeight="500" fontFamily="Outfit, system-ui" opacity={visible ? 0.7 : 0} style={{ transition: `opacity 0.5s ease ${(i + 5) * 80}ms` }}>{n.label}</text>
          )}
        </g>
      ))}
    </svg>
  );
}
